import type { ILocalizationService, INotificationService } from "@emanprague/shared-services";
import { SeverityLevel } from "@emanprague/shared-services";
import { bound } from "@frui.ts/helpers";
import { BusyWatcher, ScreenBase, watchBusy } from "@frui.ts/screens";
import { attachAutomaticValidator, hasVisibleErrors, validate } from "@frui.ts/validation";
import ReqRegistrationStep1, { ReqRegistrationStep1ValidationRules } from "entities/reqRegistrationStep1";
import { action, observable, runInAction } from "mobx";
import { unwrapErrorMessage } from "repositories/helpers";
import type LoginRepository from "repositories/loginRepository";
import queryString from "query-string";
import type SimplePortalSettings from "entities/simplePortalSettings";
import type PortalSettingsService from "services/portalSettingsService";

export type StepName = "step1" | "step2" | "step3" | "step4";

export default class RegistrationViewModel extends ScreenBase {
  static navigationName = "registration";
  navigationName = RegistrationViewModel.navigationName;
  name = "Registration";
  busyWatcher = new BusyWatcher();
  googleLoginSetting?: SimplePortalSettings;
  seznamLoginSetting?: SimplePortalSettings;

  @observable steps: Array<string> = [];

  registrationData: ReqRegistrationStep1;
  @observable currentStep: StepName = "step1";
  @observable errorMessage?: string;
  @observable omniAuthErrorMessage?: string;
  @observable successMessage?: string;
  @observable sapNotice?: string;

  constructor(
    public localization: ILocalizationService,
    private repository: LoginRepository,
    private notificationService: INotificationService,
    private portalSettingsService: PortalSettingsService
  ) {
    super();
    this.googleLoginSetting = this.portalSettingsService.getSimplePortalSettings("google_login");
    this.seznamLoginSetting = this.portalSettingsService.getSimplePortalSettings("seznam_login");

    this.name = this.translate("title");

    this.registrationData = new ReqRegistrationStep1();
    attachAutomaticValidator(this.registrationData, ReqRegistrationStep1ValidationRules);
  }

  @bound
  protected async onActivate() {
    await super.onActivate();
    this.checkRegistrationFlow();
    runInAction(() => {
      if (this.registrationData.type == "omniauth") {
        this.steps = [this.translate("step1.title"), this.translate("step2.title")];
      } else {
        this.steps = [this.translate("step1.title"), this.translate("step2.title"), this.translate("step3.title")];
      }
    });
  }

  @bound
  handleOmniAuthClick(event: React.FormEvent<HTMLFormElement>) {
    // just validate the form, set omniauth to turn off email validation
    this.registrationData.type = "omniauth";
    if (!validate(this.registrationData)) {
      event.preventDefault();
    }
  }

  checkRegistrationFlow() {
    const parsedQuery = queryString.parse(this.queryString);
    const error_code = parsedQuery?.error as string | undefined;

    if (error_code) {
      this.setOmniAuthErrorMessage(error_code);
    }

    if (parsedQuery?.type === "bankId") {
      runInAction(() => {
        this.successMessage = this.translate("bank_id_message_sent");
        this.registrationData.type = "bankId";
        this.currentStep = "step2";
      });
    }
    if (parsedQuery?.type === "omniauth") {
      const email = parsedQuery?.email as string;
      runInAction(() => {
        this.successMessage = this.translate("message_sent").replace("%email%", email);
        this.registrationData.type = "omniauth";
        this.currentStep = "step2";
      });
    }
  }

  get queryString() {
    const startUrl = window.location.href;
    const queryStart = startUrl.indexOf("?");
    if (queryStart > -1) {
      return startUrl.slice(queryStart);
    }
    return "";
  }

  get showOmniAuthButtons() {
    return this.googleLoginSetting?.active ?? this.seznamLoginSetting?.active;
  }

  get omniAuthData() {
    return {
      partner_id: this.registrationData.customerNumber?.toString(),
      account_id: this.registrationData.accountNumber?.toString(),
      mkt_agreement: this.registrationData.mktAgreement?.toString() ?? "false",
      zopAgreement: this.registrationData.zopAgreement?.toString() ?? "false",
    };
  }

  @action
  setOmniAuthErrorMessage(code?: string) {
    const message = this.localization.translateGeneral(`omniauth.errors.${code ?? "generic"}`);
    this.omniAuthErrorMessage = message;
  }

  @bound translate(key: string) {
    return this.localization.translateGeneral(`profile.registration.${key}`);
  }

  @bound translateGeneral(key: string) {
    return this.localization.translateGeneral(`general.${key}`);
  }

  get canContinue() {
    return !hasVisibleErrors(this.registrationData);
  }

  @action.bound
  @watchBusy
  async continue() {
    this.registrationData.type = "basic";
    this.errorMessage = undefined;

    if (this.registrationData.mktAgreement === undefined) {
      this.registrationData.mktAgreement = false;
    }

    if (validate(this.registrationData)) {
      const response = await this.repository.registerStep1(this.registrationData);

      runInAction(() => {
        if (response.success) {
          this.successMessage = this.translate("message_sent").replace("%email%", this.registrationData.email);
          this.currentStep = "step2";
          this.sapNotice = response.payload.sapNotice;
        } else {
          this.errorMessage = unwrapErrorMessage(response.payload);
        }
      });
    }
  }

  get canResend() {
    return this.registrationData.type === "basic";
  }

  @action.bound
  @watchBusy
  async resend() {
    if (validate(this.registrationData)) {
      this.sapNotice = undefined;
      const response = await this.repository.registerStep1(this.registrationData);

      runInAction(() => {
        if (response.success) {
          this.notificationService.addNotification(
            this.translate("resend").replace("%email%", this.registrationData.email),
            SeverityLevel.success
          );
          this.sapNotice = response.payload.sapNotice;
        } else {
          this.errorMessage = unwrapErrorMessage(response.payload);
        }
      });
    }
  }
}
